/**
 * Chat service for real-time messaging.
 * Handles rooms, messages, and attachments.
 */
import api from './api';

export interface ChatRoom {
  id: number;
  name?: string;
  type: 'DIRECT' | 'GROUP';
  description?: string;
  group_admin_id?: number;
  created_by: number;
  created_at: string;
  updated_at: string;
  last_message_at?: string;
  last_message_preview?: string;
  is_active: boolean;
  is_archived: boolean;
  avatar_url?: string;
  peer_display_name?: string;
  peer_avatar_url?: string;
  unread_count?: number;
  is_muted?: boolean;
  participants?: ChatParticipant[];
}

export interface ChatParticipant {
  id: number;
  room_id: number;
  user_id?: number;
  employee_id?: number;
  client_id?: number;
  can_send_messages: boolean;
  can_add_participants: boolean;
  is_admin: boolean;
  last_read_at?: string;
  last_read_message_id?: number;
  unread_count: number;
  is_muted: boolean;
  muted_until?: string;
  joined_at: string;
  left_at?: string;
  is_active: boolean;
}

export interface ChatMessage {
  id: number;
  room_id: number;
  sender_user_id?: number;
  sender_employee_id?: number;
  sender_client_id?: number;
  sender_buyer_id?: number;
  sender_display_name?: string;
  sender_avatar_url?: string;
  sender_role?: string; // 'Support', 'AA', or undefined
  type: 'TEXT' | 'IMAGE' | 'VOICE' | 'FILE' | 'SYSTEM';
  content?: string;
  reply_to_id?: number;
  is_edited: boolean;
  edited_at?: string;
  is_deleted: boolean;
  deleted_at?: string;
  created_at: string;
  read_by?: string;
  attachments?: ChatAttachment[];
}

export interface ChatAttachment {
  id: number;
  message_id: number;
  file_name: string;
  file_path: string;
  file_size: number;
  mime_type: string;
  duration_seconds?: number;
  width?: number;
  height?: number;
  thumbnail_path?: string;
  uploaded_at: string;
}

export interface CreateDirectRoomRequest {
  target_user_id?: number;
  target_employee_id?: number;
  target_buyer_id?: number;
}

export interface CreateGroupRoomRequest {
  name: string;
  description?: string;
  participant_user_ids?: number[];
  participant_employee_ids?: number[];
}

export interface SendMessageRequest {
  content?: string;
  type?: 'TEXT' | 'IMAGE' | 'VOICE' | 'FILE' | 'SYSTEM';
  reply_to_id?: number;
}

const chatService = {
  // ==================== Rooms ====================

  /**
   * Liste les conversations de l'utilisateur.
   */
  async listRooms(type?: 'DIRECT' | 'GROUP', isArchived: boolean = false): Promise<ChatRoom[]> {
    const params = new URLSearchParams();
    if (type) params.append('type', type);
    params.append('is_archived', isArchived.toString());

    const response = await api.get(`/chat/rooms?${params.toString()}`);
    return response.data;
  },

  /**
   * Crée ou récupère une conversation directe.
   */
  async createDirectRoom(data: CreateDirectRoomRequest): Promise<ChatRoom> {
    const response = await api.post('/chat/rooms/direct', null, { params: data });
    return response.data;
  },

  /**
   * Crée un groupe de discussion.
   */
  async createGroupRoom(data: CreateGroupRoomRequest): Promise<ChatRoom> {
    const response = await api.post('/chat/rooms/group', null, { params: data });
    return response.data;
  },

  /**
   * Met à jour une room (nom, description).
   */
  async updateRoom(roomId: number, data: { name?: string; description?: string }): Promise<ChatRoom> {
    const params = new URLSearchParams();
    if (data.name) params.append('name', data.name);
    if (data.description !== undefined) params.append('description', data.description);

    const response = await api.patch(`/chat/rooms/${roomId}?${params.toString()}`);
    return response.data;
  },

  /**
   * Archive une room.
   */
  async deleteRoom(roomId: number): Promise<void> {
    await api.delete(`/chat/rooms/${roomId}`);
  },

  // ==================== Online Status ====================

  /**
   * Récupère la liste des utilisateurs en ligne.
   */
  async getOnlineUsers(): Promise<{ online_users: number[]; total_online: number }> {
    const response = await api.get('/chat/online-users');
    return response.data;
  },

  /**
   * Récupère les utilisateurs en ligne dans une room spécifique.
   */
  async getRoomOnlineUsers(roomId: number): Promise<{ room_id: number; online_users: number[]; total_online: number }> {
    const response = await api.get(`/chat/rooms/${roomId}/online-users`);
    return response.data;
  },

  // ==================== Messages ====================

  /**
   * Liste les messages d'une room (pagination).
   */
  async listMessages(
    roomId: number,
    limit: number = 50,
    beforeId?: number
  ): Promise<ChatMessage[]> {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    if (beforeId) params.append('before_id', beforeId.toString());

    const response = await api.get(`/chat/rooms/${roomId}/messages?${params.toString()}`);
    return response.data;
  },

  /**
   * Envoie un message texte.
   */
  async sendMessage(roomId: number, data: SendMessageRequest): Promise<ChatMessage> {
    const params = new URLSearchParams();
    if (data.content) params.append('content', data.content);
    if (data.type) params.append('type', data.type);
    if (data.reply_to_id) params.append('reply_to_id', data.reply_to_id.toString());

    const response = await api.post(`/chat/rooms/${roomId}/messages?${params.toString()}`);
    return response.data;
  },

  /**
   * Upload une pièce jointe.
   */
  async uploadAttachment(roomId: number, messageId: number, file: File): Promise<ChatAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post(
      `/chat/rooms/${roomId}/messages/${messageId}/attachments`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  },

  /**
   * Modifie un message.
   */
  async editMessage(roomId: number, messageId: number, content: string): Promise<ChatMessage> {
    const params = new URLSearchParams();
    params.append('content', content);

    const response = await api.patch(`/chat/rooms/${roomId}/messages/${messageId}?${params.toString()}`);
    return response.data;
  },

  /**
   * Supprime un message.
   */
  async deleteMessage(roomId: number, messageId: number): Promise<void> {
    await api.delete(`/chat/rooms/${roomId}/messages/${messageId}`);
  },

  /**
   * Marque les messages comme lus.
   */
  async markAsRead(roomId: number, messageId?: number): Promise<void> {
    const params = new URLSearchParams();
    if (messageId) params.append('message_id', messageId.toString());

    await api.post(`/chat/rooms/${roomId}/read?${params.toString()}`);
  },

  /**
   * Récupère le total de messages non lus toutes conversations confondues.
   */
  async getUnreadCount(): Promise<{ unread_count: number }> {
    const response = await api.get('/chat/unread-count');
    return response.data;
  },

  /**
   * Get or create client support room (for clients only).
   * If client has an apporteur, the room will include both admin and apporteur.
   */
  async getOrCreateClientSupportRoom(): Promise<ChatRoom> {
    try {
      // First, get client info to check for apporteur relationship
      const clientResponse = await api.get('/auth/me');
      const clientData = clientResponse.data;
      
      // Create room with apporteur info if available
      const params = new URLSearchParams();
      if (clientData.admin_id) {
        params.append('include_apporteur', 'true');
        params.append('apporteur_id', clientData.admin_id.toString());
      }
      
      const response = await api.get(`/chat/rooms/client-support?${params.toString()}`);
      return response.data;
    } catch (error) {
      // Fallback to standard support room creation if enhanced version fails
      console.warn('Enhanced support room creation failed, falling back to standard:', error);
      const response = await api.get('/chat/rooms/client-support');
      return response.data;
    }
  },

  /**
   * Send message with sender role attribution for internal visibility.
   */
  async sendMessageWithAttribution(roomId: number, data: SendMessageRequest & { sender_role?: string }): Promise<ChatMessage> {
    const params = new URLSearchParams();
    if (data.content) params.append('content', data.content);
    if (data.type) params.append('type', data.type);
    if (data.reply_to_id) params.append('reply_to_id', data.reply_to_id.toString());
    if (data.sender_role) params.append('sender_role', data.sender_role);

    const response = await api.post(`/chat/rooms/${roomId}/messages?${params.toString()}`);
    return response.data;
  },
};

export default chatService;
